// Placeworthy — MOCK DATA (frontend-only teaser)
// Global-ready: Country > Region (province/state) > City > Neighbourhood.
// All content below is illustrative sample data.

export const IMAGES = {
  heroInterior: "https://images.unsplash.com/photo-1578474846511-04ba529f0b88",
  restaurant1: "https://images.pexels.com/photos/31071253/pexels-photo-31071253.jpeg",
  restaurant2: "https://images.pexels.com/photos/29962487/pexels-photo-29962487.jpeg",
  restaurant3: "https://images.pexels.com/photos/30479386/pexels-photo-30479386.jpeg",
  moody1: "https://images.unsplash.com/photo-1469234496837-d0101f54be3e",
  moody2: "https://images.unsplash.com/photo-1514470917599-d168319c9edb",
  moody3: "https://images.pexels.com/photos/4997854/pexels-photo-4997854.jpeg",
  moody4: "https://images.pexels.com/photos/4577715/pexels-photo-4577715.jpeg",
  dish1: "https://images.unsplash.com/photo-1467003909585-2f8a72700288",
  dish2: "https://images.pexels.com/photos/1327393/pexels-photo-1327393.jpeg",
  dish3: "https://images.pexels.com/photos/23644633/pexels-photo-23644633.jpeg",
  dish4: "https://images.unsplash.com/photo-1627858034922-72a657d6b3c2",
  chef1: "https://images.pexels.com/photos/11322617/pexels-photo-11322617.jpeg",
  chef2: "https://images.pexels.com/photos/36430034/pexels-photo-36430034.jpeg",
  dishClose1: "https://images.unsplash.com/photo-1557079604-d28080618be0",
  dishClose2: "https://images.pexels.com/photos/19671300/pexels-photo-19671300.jpeg",
  bar1: "https://images.pexels.com/photos/16859959/pexels-photo-16859959.jpeg",
  bar2: "https://images.pexels.com/photos/36366519/pexels-photo-36366519.jpeg",
  cafe1: "https://images.pexels.com/photos/20825644/pexels-photo-20825644.jpeg",
  cafe2: "https://images.pexels.com/photos/26673696/pexels-photo-26673696.jpeg",
  cafe3: "https://images.unsplash.com/photo-1545418314-7ce0b9b53901",
  cafe4: "https://images.unsplash.com/photo-1521017432531-fbd92d768814",
  cafe5: "https://images.unsplash.com/photo-1453614512568-c4024d13c247",
  cafe6: "https://images.pexels.com/photos/5373256/pexels-photo-5373256.jpeg",
};

export const CRITERIA = [
  { key: "food", label: "Food", blurb: "Taste, technique, sourcing, and execution — judged honestly against what the kitchen is trying to accomplish." },
  { key: "atmosphere", label: "Atmosphere", blurb: "Setting, energy, noise level, and whether the space earns the experience it promises." },
  { key: "service", label: "Service", blurb: "Attentiveness, knowledge, pace, and the kind of care that makes guests feel welcome." },
  { key: "value", label: "Value", blurb: "Whether what you pay matches what you receive — not cheap versus expensive, but fair versus not." },
  { key: "experience", label: "Experience", blurb: "The overall verdict: would we send someone we care about here? That is the only question that matters." },
];

export function verdictFor(score) {
  if (score >= 9) return { label: "Placeworthy", tone: "top" };
  if (score >= 8) return { label: "Worth It", tone: "good" };
  if (score >= 6.5) return { label: "Worth a Visit", tone: "mid" };
  if (score >= 5) return { label: "Situational", tone: "low" };
  return { label: "Skip It", tone: "skip" };
}

export function avgScore(scores) {
  const vals = Object.values(scores);
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

// ————— PLACEWORTHY DISTINCTION (1–3 marks) —————
export const DISTINCTIONS = {
  1: { label: "Placeworthy", sub: "Recommended" },
  2: { label: "Placeworthy Exceptional", sub: "Outstanding" },
  3: { label: "Placeworthy Destination", sub: "Worth going out of your way for" },
};

export function distinctionOf(place) {
  if (place && typeof place.distinction === "number") return place.distinction;
  if (!place || !place.scores) return 0;
  const avg = avgScore(place.scores);
  if (avg >= 9) return 3;
  if (avg >= 8) return 2;
  if (avg >= 7) return 1;
  return 0;
}

// Editorial accolade badges (separate from the 1–3 mark distinction)
export const ACCOLADES = ["Editor's Choice", "Best New", "Hidden Gem", "Destination", "People's Choice"];

export const PLACES = [
  // ————— CANADA · ALBERTA · CALGARY —————
  {
    id: "1", slug: "ox-and-angela", name: "Ox & Angela", category: "Restaurant",
    cuisine: "Spanish · Wood-fire", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Beltline",
    price: "$$$", image: IMAGES.heroInterior, gallery: [IMAGES.dish1, IMAGES.moody1, IMAGES.dish3],
    scores: { food: 9, atmosphere: 8, service: 8, value: 7, experience: 9 },
    excerpt: "A long-standing Beltline room that still earns its reputation. The wood-fired cooking is precise and the wine list has genuine personality.",
    author: "M. Delacroix", date: "Jul 2025", featured: true,
    tags: ["Wine List", "Date Night", "Tasting Menu"],
    body: [
      "There are rooms that coast on reputation and rooms that keep re-earning it. Ox & Angela belongs firmly to the second category. On a quiet Tuesday, with the wood oven throwing heat across the pass, the kitchen turned out plate after plate of unfussy, confident cooking.",
      "The charred octopus arrived with a smear of romesco that had clearly been made that morning — bright, nutty, alive. The wood-fired lamb was the centrepiece: rested properly, seasoned fearlessly, and served with nothing it did not need.",
      "Service was warm without being performative. The room hums at the perfect volume. This is a splurge, and it knows it — but a splurge that delivers.",
    ],
  },
  {
    id: "2", slug: "the-hearth-inglewood", name: "The Hearth", category: "Restaurant",
    cuisine: "Modern Canadian", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Inglewood",
    price: "$$$", image: IMAGES.restaurant2, gallery: [IMAGES.dish2, IMAGES.chef1, IMAGES.dishClose1],
    scores: { food: 9, atmosphere: 9, service: 9, value: 8, experience: 9 },
    excerpt: "The most complete dining experience we have found in Calgary this year. Seasonal, precise, and quietly generous from the first pour to the last.",
    author: "R. Kowalski", date: "Jun 2025", featured: true,
    tags: ["Seasonal", "Chef's Counter", "Special Occasion"],
    body: [
      "Every so often a restaurant reminds you why the whole enterprise is worth taking seriously. The Hearth is that restaurant right now. From the moment you sit down, everyone in the room is pulling in the same direction.",
      "The tasting progression leaned hard into local produce: preserved berries, dry-aged bison, root vegetables coaxed into something greater than the sum of their parts. Nothing felt like it was chasing a trend.",
      "It is not cheap, but it never once felt like it was reaching into your pocket. The kind of place we would send someone we care about — the definition, for us, of Placeworthy.",
    ],
  },
  {
    id: "3", slug: "camber-coffee", name: "Camber Coffee", category: "Café",
    cuisine: "Third-wave coffee", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Kensington",
    price: "$", image: IMAGES.cafe3, gallery: [IMAGES.cafe1, IMAGES.cafe5, IMAGES.dishClose2],
    scores: { food: 8, atmosphere: 8, service: 7, value: 8, experience: 8 },
    excerpt: "A serious coffee bar that never forgets it is also a neighbourhood living room. The pour-overs are meticulous; the morning bun is a quiet triumph.",
    author: "S. Nakamura", date: "Jun 2025", featured: false,
    tags: ["Coffee", "Pastry", "Work-Friendly"],
    body: [
      "Kensington has no shortage of coffee shops, which makes it all the more impressive that Camber has carved out a genuine identity. The house espresso is dialled with obvious care.",
      "The pastry program punches well above its weight. Seating is tight at peak hours, but the fundamentals are sound.",
      "For the price, this is one of the easiest recommendations in the city. Worth the detour.",
    ],
  },
  {
    id: "4", slug: "marquee-cocktail-room", name: "Marquee Cocktail Room", category: "Bar",
    cuisine: "Craft cocktails", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Downtown",
    price: "$$$", image: IMAGES.bar1, gallery: [IMAGES.bar2, IMAGES.moody2, IMAGES.moody3],
    scores: { food: 7, atmosphere: 9, service: 8, value: 7, experience: 8 },
    excerpt: "A grown-up cocktail room with real bartending chops and a soundtrack that knows when to whisper. The best low-lit seat in the core.",
    author: "M. Delacroix", date: "May 2025", featured: true,
    tags: ["Cocktails", "Late Night", "Date Night"],
    body: [
      "The cocktail scene has matured fast, and Marquee is proof. The list is short, seasonal, and confident — no forty-ingredient theatrics, just precise drinks built on good spirits and better ideas.",
      "The room does most of the heavy lifting: banquettes deep enough to disappear into, lighting engineered for flattery.",
      "You are paying downtown prices, but you are also getting the most convincing argument for staying out one more round.",
    ],
  },
  {
    id: "5", slug: "field-and-flour", name: "Field & Flour", category: "Bakery",
    cuisine: "Bakery · Café", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Bridgeland",
    price: "$$", image: IMAGES.cafe2, gallery: [IMAGES.dish4, IMAGES.cafe6, IMAGES.dishClose2],
    scores: { food: 9, atmosphere: 7, service: 8, value: 9, experience: 8 },
    excerpt: "The best argument for waking up early. The sourdough has a proper crust and a story, and the queue moves with grace.",
    author: "R. Kowalski", date: "May 2025", featured: false,
    tags: ["Bakery", "Sourdough", "Breakfast"],
    body: [
      "A great bakery is a neighbourhood asset, and Field & Flour has become exactly that. The sourdough is the headline — blistered crust, open crumb, a tang that lingers without shouting.",
      "Beyond the bread, the savoury galettes and seasonal tarts are quietly excellent. Value is genuinely strong.",
      "The space is small and gets busy, but the line is part of the ritual.",
    ],
  },
  {
    id: "6", slug: "north-bridge-supper", name: "North Bridge Supper Club", category: "Restaurant",
    cuisine: "Steak · Seafood", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Mission",
    price: "$$$$", image: IMAGES.restaurant3, gallery: [IMAGES.moody4, IMAGES.dish1, IMAGES.chef2],
    scores: { food: 8, atmosphere: 8, service: 7, value: 6, experience: 7 },
    excerpt: "Ambition and old-school glamour in equal measure. The kitchen mostly delivers; the bill asks a great deal in return.",
    author: "S. Nakamura", date: "Apr 2025", featured: false,
    tags: ["Steak", "Special Occasion", "Big Group"],
    body: [
      "North Bridge wants to be the room you take people to impress them, and on the food alone it often succeeds. The dry-aged ribeye was faultless.",
      "Where it wobbles is the gap between price and polish. At this tier, service should be seamless, and ours was merely good.",
      "A genuinely good time and a genuinely large bill. Worth a visit for the occasion.",
    ],
  },
  {
    id: "7", slug: "saffron-and-smoke", name: "Saffron & Smoke", category: "Restaurant",
    cuisine: "Modern Indian", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Beltline",
    price: "$$$", image: IMAGES.restaurant1, gallery: [IMAGES.dish3, IMAGES.dishClose1, IMAGES.chef1],
    scores: { food: 9, atmosphere: 8, service: 8, value: 8, experience: 9 },
    excerpt: "Spice handled with the confidence of a kitchen that has nothing to prove. The tandoor work alone is worth the trip across town.",
    author: "M. Delacroix", date: "Apr 2025", featured: true,
    tags: ["Tandoor", "Spice", "Date Night"],
    body: [
      "Saffron & Smoke treats Indian cooking as living, evolving cuisine — and the results are thrilling. The tandoor is the heart of the kitchen.",
      "The lamb chops were charred, smoky, and fearlessly spiced; the black dal had clearly simmered for the better part of a day.",
      "Service is knowledgeable and generous with guidance. For the quality on the plate, the value is excellent.",
    ],
  },
  {
    id: "8", slug: "harbourline-oyster", name: "Harbourline Oyster Bar", category: "Bar",
    cuisine: "Raw bar · Wine", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Inglewood",
    price: "$$$", image: IMAGES.bar2, gallery: [IMAGES.dish2, IMAGES.moody1, IMAGES.dishClose2],
    scores: { food: 8, atmosphere: 8, service: 8, value: 7, experience: 8 },
    excerpt: "A landlocked oyster bar that has no business being this good. Impeccable shucking, a smart by-the-glass list, and a room built for lingering.",
    author: "R. Kowalski", date: "Mar 2025", featured: false,
    tags: ["Oysters", "Wine", "Happy Hour"],
    body: [
      "It takes nerve to open a serious oyster bar this far from the coast, and Harbourline backs it up. The selection was fresh and well-kept, the shucking clean.",
      "The wine program is the quiet star — a by-the-glass list that rewards curiosity without punishing your wallet.",
      "Not cheap, but honest about what it is. A lovely place to lose an evening one dozen at a time.",
    ],
  },
  {
    id: "9", slug: "prairie-larder", name: "Prairie Larder", category: "Café",
    cuisine: "Brunch · Café", country: "Canada", region: "Alberta", city: "Calgary", neighborhood: "Marda Loop",
    price: "$$", image: IMAGES.cafe4, gallery: [IMAGES.dish4, IMAGES.cafe1, IMAGES.dishClose1],
    scores: { food: 7, atmosphere: 8, service: 7, value: 8, experience: 7 },
    excerpt: "A bright, generous brunch room that does the classics properly. Nothing revolutionary — just reliably good, which is its own achievement.",
    author: "S. Nakamura", date: "Mar 2025", featured: false,
    tags: ["Brunch", "Family", "Coffee"],
    body: [
      "Prairie Larder is a big reason Marda Loop has become one of the city's best brunch neighbourhoods. The room is bright and unpretentious.",
      "The cooking plays it straight and plays it well: properly poached eggs, hash with actual crunch, coffee that could hold its own in a dedicated café.",
      "Weekend waits are real, but the value is fair and the welcome is warm.",
    ],
  },
  // ————— CANADA · ONTARIO · TORONTO —————
  {
    id: "13", slug: "grange-and-vine", name: "Grange & Vine", category: "Restaurant",
    cuisine: "Wine bar · Small plates", country: "Canada", region: "Ontario", city: "Toronto", neighborhood: "Queen West",
    price: "$$$", image: IMAGES.moody2, gallery: [IMAGES.dish3, IMAGES.bar1, IMAGES.dishClose1],
    scores: { food: 8, atmosphere: 9, service: 8, value: 7, experience: 8 },
    excerpt: "Queen West's most quietly confident wine room. The list is thrilling and the small plates keep pace without trying too hard.",
    author: "R. Kowalski", date: "Jul 2025", featured: true,
    tags: ["Wine List", "Small Plates", "Date Night"],
    body: [
      "Toronto has no shortage of wine bars, but Grange & Vine understands the assignment better than most. The list is a genuine education, poured by people who actually want to talk about it.",
      "The small plates are precise and seasonal — a whipped cod roe, a plate of jamón, a tart that changes weekly.",
      "It gets loud and it gets busy, both in the best way. One of the most reliable good nights in the city.",
    ],
  },
  {
    id: "14", slug: "queen-west-coffee", name: "Queen West Coffee Co.", category: "Café",
    cuisine: "Specialty coffee", country: "Canada", region: "Ontario", city: "Toronto", neighborhood: "Trinity Bellwoods",
    price: "$", image: IMAGES.cafe5, gallery: [IMAGES.cafe1, IMAGES.dishClose2, IMAGES.cafe6],
    scores: { food: 7, atmosphere: 8, service: 8, value: 8, experience: 8 },
    excerpt: "A tiny corner café that takes its coffee seriously and its customers even more so. The flat white is faultless.",
    author: "S. Nakamura", date: "Jun 2025", featured: false,
    tags: ["Coffee", "Pastry", "Neighbourhood"],
    body: [
      "There is a particular kind of café that becomes the anchor of a block, and Queen West Coffee Co. is exactly that for Trinity Bellwoods.",
      "The espresso is bright and confident; the pastries come from a bakery two doors down and taste like it.",
      "Nothing showy here — just a very good café doing the fundamentals right, every day.",
    ],
  },
  // ————— CANADA · BRITISH COLUMBIA · VANCOUVER —————
  {
    id: "15", slug: "harbour-and-cedar", name: "Harbour & Cedar", category: "Restaurant",
    cuisine: "Pacific Northwest · Seafood", country: "Canada", region: "British Columbia", city: "Vancouver", neighborhood: "Gastown",
    price: "$$$$", image: IMAGES.restaurant3, gallery: [IMAGES.dish2, IMAGES.moody4, IMAGES.chef2],
    scores: { food: 9, atmosphere: 9, service: 8, value: 7, experience: 9 },
    excerpt: "A love letter to the coast. The seafood is pristine, the room is beautiful, and the whole thing feels effortless in a way that takes enormous effort.",
    author: "M. Delacroix", date: "Jul 2025", featured: true,
    tags: ["Seafood", "Special Occasion", "Ocean View"],
    body: [
      "Vancouver's relationship with the ocean is written all over Harbour & Cedar's menu, and the kitchen honours it with real skill. The spot prawns were barely touched by heat and all the better for it.",
      "The room looks out over the water and knows exactly how lucky it is, without ever coasting on the view.",
      "An expensive evening, but a genuinely memorable one. Placeworthy, without hesitation.",
    ],
  },
  {
    id: "16", slug: "gastown-pour-house", name: "Gastown Pour House", category: "Bar",
    cuisine: "Cocktails · Natural wine", country: "Canada", region: "British Columbia", city: "Vancouver", neighborhood: "Gastown",
    price: "$$$", image: IMAGES.bar2, gallery: [IMAGES.bar1, IMAGES.moody3, IMAGES.dishClose2],
    scores: { food: 7, atmosphere: 8, service: 8, value: 7, experience: 8 },
    excerpt: "A handsome corner bar with a serious list and an easy attitude. Exactly the kind of room a great neighbourhood deserves.",
    author: "R. Kowalski", date: "May 2025", featured: false,
    tags: ["Cocktails", "Natural Wine", "Casual"],
    body: [
      "Gastown's cobblestones have seen plenty of bars come and go, but Pour House feels built to last. The cocktail list is tight and the natural wine selection is genuinely thoughtful.",
      "The snacks are more than an afterthought — good bread, better cheese, a rotating small plate worth ordering.",
      "Warm, unpretentious, and easy to fall for.",
    ],
  },
  // ————— CANADA · QUEBEC · MONTREAL —————
  {
    id: "17", slug: "maison-lumiere", name: "Maison Lumière", category: "Restaurant",
    cuisine: "Modern French", country: "Canada", region: "Quebec", city: "Montreal", neighborhood: "Old Montreal",
    price: "$$$$", image: IMAGES.restaurant1, gallery: [IMAGES.dish1, IMAGES.moody1, IMAGES.dish3],
    scores: { food: 9, atmosphere: 9, service: 9, value: 7, experience: 9 },
    excerpt: "Old Montreal glamour with a kitchen to match. Technique, warmth and a genuine sense of occasion in every course.",
    author: "M. Delacroix", date: "Jun 2025", featured: true,
    tags: ["Tasting Menu", "French", "Special Occasion"],
    body: [
      "Montreal knows how to do a grand dining room, and Maison Lumière is a masterclass. The cooking is classically French but never stuffy.",
      "Service moves with the quiet choreography of a team that has done this a thousand times and still cares about the thousand-and-first.",
      "It is a splurge and an occasion — and one of the finest meals we have had this year.",
    ],
  },
  // ————— UNITED STATES · NEW YORK · NEW YORK CITY —————
  {
    id: "18", slug: "the-bowery-table", name: "The Bowery Table", category: "Restaurant",
    cuisine: "New American", country: "United States", region: "New York", city: "New York City", neighborhood: "Bowery",
    price: "$$$", image: IMAGES.restaurant2, gallery: [IMAGES.dish2, IMAGES.chef1, IMAGES.moody2],
    scores: { food: 9, atmosphere: 8, service: 8, value: 7, experience: 8 },
    excerpt: "A downtown room firing on all cylinders. Confident New American cooking, a buzzing bar, and a menu that rewards ordering widely.",
    author: "R. Kowalski", date: "Jul 2025", featured: true,
    tags: ["New American", "Buzzy", "Date Night"],
    body: [
      "The Bowery Table has that particular downtown energy — packed, loud, alive — backed up by a kitchen that clearly knows what it is doing.",
      "The dry-aged duck for two is the move; the vegetable dishes are just as considered as the proteins, which is rarer than it should be.",
      "It is a New York price for a New York night, and on our visit it earned every dollar.",
    ],
  },
  {
    id: "19", slug: "brooklyn-roasters", name: "Brooklyn Roasters", category: "Café",
    cuisine: "Coffee · Roastery", country: "United States", region: "New York", city: "New York City", neighborhood: "Williamsburg",
    price: "$$", image: IMAGES.cafe1, gallery: [IMAGES.cafe5, IMAGES.dishClose2, IMAGES.cafe6],
    scores: { food: 7, atmosphere: 8, service: 7, value: 7, experience: 7 },
    excerpt: "A roastery-café that takes the bean seriously without the attitude. The single-origin flight is a lovely way to spend a slow morning.",
    author: "S. Nakamura", date: "May 2025", featured: false,
    tags: ["Coffee", "Roastery", "Work-Friendly"],
    body: [
      "Williamsburg has a café on every corner, so it means something that Brooklyn Roasters stands out. The in-house roasting is the draw, and it shows in the cup.",
      "The space is big enough to actually find a seat, which in this neighbourhood is close to a miracle.",
      "Solid pastries, friendly-enough service, and coffee worth going out of your way for.",
    ],
  },
  // ————— UNITED STATES · TEXAS · AUSTIN —————
  {
    id: "20", slug: "smoke-and-barrel", name: "Smoke & Barrel", category: "Restaurant",
    cuisine: "Texas BBQ", country: "United States", region: "Texas", city: "Austin", neighborhood: "East Austin",
    price: "$$", image: IMAGES.chef2, gallery: [IMAGES.dish3, IMAGES.moody4, IMAGES.dish1],
    scores: { food: 9, atmosphere: 7, service: 7, value: 9, experience: 8 },
    excerpt: "Brisket worth queuing for at dawn. Old-school Texas smoke done with obsessive care and priced like it still respects you.",
    author: "M. Delacroix", date: "Jun 2025", featured: true,
    tags: ["BBQ", "Brisket", "Casual"],
    body: [
      "The line forms before the doors open, and by now you know what that means in Texas. Smoke & Barrel earns its queue.",
      "The brisket is the headline — a proper bark, a clean smoke ring, fat rendered to silk. The ribs are nearly as good.",
      "It is picnic tables and butcher paper, and it is glorious. Extraordinary value for cooking this serious.",
    ],
  },
  {
    id: "21", slug: "east-sixth-social", name: "East Sixth Social", category: "Bar",
    cuisine: "Cocktails · Live music", country: "United States", region: "Texas", city: "Austin", neighborhood: "East Sixth",
    price: "$$", image: IMAGES.bar1, gallery: [IMAGES.bar2, IMAGES.moody3, IMAGES.dishClose2],
    scores: { food: 6, atmosphere: 9, service: 7, value: 8, experience: 7 },
    excerpt: "A rowdy, warm-hearted bar with real cocktails and even better music. The kind of place Austin does better than anyone.",
    author: "R. Kowalski", date: "Apr 2025", featured: false,
    tags: ["Cocktails", "Live Music", "Late Night"],
    body: [
      "East Sixth is Austin at its most Austin, and this bar is one of its best rooms. The drinks are genuinely good — not just an afterthought to the band.",
      "The music is the point, and the booking is smart enough to keep locals coming back.",
      "The kitchen is basic, but you are not here for dinner. You are here for the night, and it delivers.",
    ],
  },
  // ————— UNITED STATES · CALIFORNIA · LOS ANGELES —————
  {
    id: "22", slug: "sunset-and-vine-kitchen", name: "Sunset & Vine Kitchen", category: "Restaurant",
    cuisine: "California · Seasonal", country: "United States", region: "California", city: "Los Angeles", neighborhood: "Silver Lake",
    price: "$$$", image: IMAGES.dish1, gallery: [IMAGES.dish4, IMAGES.chef1, IMAGES.moody1],
    scores: { food: 8, atmosphere: 8, service: 8, value: 7, experience: 8 },
    excerpt: "Sun-drenched California cooking that lets the produce do the talking. Effortless in the way only LA can pull off.",
    author: "S. Nakamura", date: "Jun 2025", featured: true,
    tags: ["Seasonal", "Patio", "Brunch"],
    body: [
      "Sunset & Vine is the platonic ideal of a California restaurant — market produce, a beautiful patio, and a kitchen confident enough to keep things simple.",
      "The wood-grilled vegetables were the surprise standout; the fish was cooked with a light, sure hand.",
      "It is not reinventing anything, and it does not need to. A lovely, sunny room worth the drive.",
    ],
  },
  // ————— UNITED STATES · ILLINOIS · CHICAGO —————
  {
    id: "23", slug: "loop-and-larder", name: "Loop & Larder", category: "Restaurant",
    cuisine: "Midwestern · Seasonal", country: "United States", region: "Illinois", city: "Chicago", neighborhood: "The Loop",
    price: "$$$", image: IMAGES.restaurant1, gallery: [IMAGES.dish2, IMAGES.dishClose1, IMAGES.chef2],
    scores: { food: 8, atmosphere: 8, service: 8, value: 8, experience: 8 },
    excerpt: "Big-shouldered Midwestern cooking with real finesse. Generous, seasonal, and priced with genuine fairness for the quality.",
    author: "R. Kowalski", date: "May 2025", featured: false,
    tags: ["Seasonal", "Group-Friendly", "Value"],
    body: [
      "Chicago rewards restaurants that are both ambitious and unpretentious, and Loop & Larder threads that needle beautifully.",
      "Portions are generous without being careless, and the seasonal menu clearly tracks what is actually good right now.",
      "Excellent value for the quality on the plate. A dependable, deeply likeable room.",
    ],
  },
  // ————— UNITED KINGDOM · ENGLAND · LONDON —————
  {
    id: "24", slug: "the-marylebone-room", name: "The Marylebone Room", category: "Restaurant",
    cuisine: "Modern British", country: "United Kingdom", region: "England", city: "London", neighborhood: "Marylebone",
    price: "$$$$", image: IMAGES.moody1, gallery: [IMAGES.dish1, IMAGES.chef1, IMAGES.dishClose1],
    scores: { food: 9, atmosphere: 9, service: 9, value: 7, experience: 9 },
    excerpt: "A grown-up London room that does modern British cooking with quiet mastery. Impeccable from the bread service onward.",
    author: "M. Delacroix", date: "Jul 2025", featured: true,
    tags: ["Modern British", "Special Occasion", "Wine List"],
    body: [
      "London has more good restaurants than any one person could visit, which makes standing out genuinely hard. The Marylebone Room manages it through sheer consistency and care.",
      "The cooking is confidently British — dry-aged beef, native oysters, a faultless custard tart — without a hint of gimmick.",
      "An expensive evening, but one delivered with the kind of polish that justifies it. Placeworthy.",
    ],
  },
  // ————— FRANCE · ÎLE-DE-FRANCE · PARIS —————
  {
    id: "25", slug: "cafe-des-lumieres", name: "Café des Lumières", category: "Café",
    cuisine: "Parisian bistro · Café", country: "France", region: "Île-de-France", city: "Paris", neighborhood: "Le Marais",
    price: "$$", image: IMAGES.cafe6, gallery: [IMAGES.cafe1, IMAGES.dishClose2, IMAGES.cafe5],
    scores: { food: 8, atmosphere: 9, service: 7, value: 8, experience: 8 },
    excerpt: "The platonic ideal of a Marais café — great coffee, a proper croque, and a terrace made for watching Paris go by.",
    author: "S. Nakamura", date: "Jun 2025", featured: true,
    tags: ["Café", "Terrace", "Breakfast"],
    body: [
      "Paris does not lack for cafés, but Café des Lumières understands that atmosphere and quality are not a trade-off. The coffee is genuinely good; the pastries are better.",
      "The terrace is the whole point on a warm morning, and the service, while unhurried, is warm once you settle in.",
      "Unfussy, lovely, and very fairly priced for the postcode.",
    ],
  },
  // ————— ITALY · LAZIO · ROME —————
  {
    id: "26", slug: "trattoria-sette", name: "Trattoria Sette", category: "Restaurant",
    cuisine: "Roman · Trattoria", country: "Italy", region: "Lazio", city: "Rome", neighborhood: "Trastevere",
    price: "$$$", image: IMAGES.restaurant1, gallery: [IMAGES.dish3, IMAGES.moody4, IMAGES.dish1],
    scores: { food: 9, atmosphere: 8, service: 8, value: 8, experience: 9 },
    excerpt: "Roman classics cooked with total conviction. The cacio e pepe alone is worth the flight, and the room is pure Trastevere charm.",
    author: "M. Delacroix", date: "Jun 2025", featured: true,
    tags: ["Pasta", "Roman", "Family"],
    body: [
      "Trastevere is thick with trattorias, many coasting on tourists. Trattoria Sette is the real thing — a family room cooking Roman classics the way they should be.",
      "The cacio e pepe was flawless: glossy, peppery, emulsified to perfection. The carbonara was nearly its equal.",
      "Warm, generous and honestly priced. Exactly what a great trattoria should be.",
    ],
  },
  // ————— AUSTRALIA · NEW SOUTH WALES · SYDNEY —————
  {
    id: "27", slug: "harbourside-kitchen", name: "Harbourside Kitchen", category: "Restaurant",
    cuisine: "Modern Australian · Seafood", country: "Australia", region: "New South Wales", city: "Sydney", neighborhood: "Surry Hills",
    price: "$$$$", image: IMAGES.restaurant3, gallery: [IMAGES.dish2, IMAGES.chef2, IMAGES.moody1],
    scores: { food: 9, atmosphere: 9, service: 8, value: 7, experience: 9 },
    excerpt: "Sydney sunshine on a plate. Pristine seafood, native ingredients, and a room that makes the most of the harbour city's easy glamour.",
    author: "R. Kowalski", date: "Jul 2025", featured: true,
    tags: ["Seafood", "Modern Australian", "Special Occasion"],
    body: [
      "Sydney's dining scene runs on great produce and even better light, and Harbourside Kitchen bottles both. The seafood is faultless and the native ingredients are used with real intelligence.",
      "The room is polished without being stiff — the sort of easy confidence Australian restaurants do so well.",
      "A splurge, but a genuinely joyful one. Among the best meals in the city.",
    ],
  },
  // ————— AUSTRALIA · VICTORIA · MELBOURNE —————
  {
    id: "28", slug: "fitzroy-roasters", name: "Fitzroy Roasters", category: "Café",
    cuisine: "Specialty coffee", country: "Australia", region: "Victoria", city: "Melbourne", neighborhood: "Fitzroy",
    price: "$$", image: IMAGES.cafe2, gallery: [IMAGES.cafe1, IMAGES.dish4, IMAGES.dishClose2],
    scores: { food: 8, atmosphere: 8, service: 8, value: 8, experience: 8 },
    excerpt: "Melbourne invented modern café culture, and Fitzroy Roasters is a reminder why. Impeccable coffee and a brunch that means it.",
    author: "S. Nakamura", date: "May 2025", featured: false,
    tags: ["Coffee", "Brunch", "Neighbourhood"],
    body: [
      "Melbourne sets the global standard for cafés, so praise here is not given lightly. Fitzroy Roasters earns it — the coffee is superb and the brunch is genuinely creative.",
      "The room is all warm brick and greenery, buzzing without being unbearable.",
      "One of the easiest recommendations in a city full of them.",
    ],
  },
  // ————— JAPAN · TOKYO · TOKYO —————
  {
    id: "29", slug: "omakase-nishi", name: "Omakase Nishi", category: "Restaurant",
    cuisine: "Sushi · Omakase", country: "Japan", region: "Tokyo", city: "Tokyo", neighborhood: "Ginza",
    price: "$$$$", image: IMAGES.dishClose1, gallery: [IMAGES.dish3, IMAGES.dish1, IMAGES.moody2],
    scores: { food: 10, atmosphere: 8, service: 9, value: 7, experience: 9 },
    excerpt: "A Ginza sushi counter operating at the very top of the craft. Precise, serene, and utterly transporting from the first piece.",
    author: "M. Delacroix", date: "Jul 2025", featured: true,
    tags: ["Sushi", "Omakase", "Counter"],
    body: [
      "There is a particular stillness to a great Tokyo sushi counter, and Omakase Nishi has it in full. Every piece is a study in restraint and precision.",
      "The rice — always the tell — is seasoned and tempered with obvious mastery. The fish speaks for itself.",
      "It is expensive and it is worth it. One of the finest meals we have reviewed, anywhere.",
    ],
  },
  {
    id: "30", slug: "golden-gai-highball", name: "Golden Gai Highball", category: "Bar",
    cuisine: "Whisky · Highballs", country: "Japan", region: "Tokyo", city: "Tokyo", neighborhood: "Shinjuku",
    price: "$$", image: IMAGES.bar2, gallery: [IMAGES.bar1, IMAGES.moody3, IMAGES.dishClose2],
    scores: { food: 6, atmosphere: 9, service: 8, value: 8, experience: 8 },
    excerpt: "A tiny Shinjuku counter with a serious whisky wall and the best highball in the neighbourhood. Six seats, endless charm.",
    author: "R. Kowalski", date: "Apr 2025", featured: false,
    tags: ["Whisky", "Late Night", "Intimate"],
    body: [
      "Golden Gai is all about the tiny bar, and this one is a jewel. Six seats, a wall of whisky, and a bartender who takes the highball as seriously as any cocktail.",
      "It is snug to the point of intimacy, and that is exactly the appeal.",
      "Go early, be patient for a seat, and settle in. Pure Tokyo magic.",
    ],
  },
  // ————— SINGAPORE · SINGAPORE · SINGAPORE —————
  {
    id: "31", slug: "tiong-bahru-table", name: "Tiong Bahru Table", category: "Restaurant",
    cuisine: "Modern Singaporean", country: "Singapore", region: "Singapore", city: "Singapore", neighborhood: "Tiong Bahru",
    price: "$$$", image: IMAGES.dish4, gallery: [IMAGES.dish3, IMAGES.chef1, IMAGES.moody1],
    scores: { food: 9, atmosphere: 8, service: 8, value: 8, experience: 9 },
    excerpt: "Hawker soul with fine-dining precision. A love letter to Singapore's food heritage, plated with real ambition.",
    author: "S. Nakamura", date: "Jun 2025", featured: true,
    tags: ["Singaporean", "Modern", "Date Night"],
    body: [
      "Singapore's food identity runs deep, and Tiong Bahru Table honours it while pushing it forward. The chilli crab reimagined as a refined course was a genuine thrill.",
      "The kitchen clearly reveres the hawker classics it draws on, and the flavours never lose their punch to the polish.",
      "Confident, delicious and fairly priced for the ambition. A standout in a serious food city.",
    ],
  },
];

// ————— GEOGRAPHY —————
export const COUNTRIES = ["Canada", "United States", "United Kingdom", "France", "Italy", "Australia", "Japan", "Singapore"];

export const REGIONS = [
  { country: "Canada", name: "Alberta", tagline: "Where it all began — a fast-rising prairie food scene.", image: IMAGES.heroInterior },
  { country: "Canada", name: "British Columbia", tagline: "Coastal cooking and Pacific Northwest bounty.", image: IMAGES.restaurant3 },
  { country: "Canada", name: "Ontario", tagline: "Toronto's restless, world-class dining energy.", image: IMAGES.moody2 },
  { country: "Canada", name: "Quebec", tagline: "Old-world glamour and modern French ambition.", image: IMAGES.restaurant1 },
  { country: "United States", name: "New York", tagline: "The city that never stops ordering another course.", image: IMAGES.restaurant2 },
  { country: "United States", name: "California", tagline: "Sun, seasonality and effortless West Coast cooking.", image: IMAGES.dish1 },
  { country: "United States", name: "Texas", tagline: "Smoke, live music and generous hospitality.", image: IMAGES.chef2 },
  { country: "United States", name: "Illinois", tagline: "Big-shouldered Midwestern cooking with finesse.", image: IMAGES.dish2 },
  { country: "United Kingdom", name: "England", tagline: "London's endless, world-leading restaurant scene.", image: IMAGES.moody1 },
  { country: "France", name: "Île-de-France", tagline: "Paris — the city that wrote the rules of dining.", image: IMAGES.cafe6 },
  { country: "Italy", name: "Lazio", tagline: "Rome's soulful trattorias and timeless classics.", image: IMAGES.dish3 },
  { country: "Australia", name: "New South Wales", tagline: "Sydney sunshine, harbour views and pristine seafood.", image: IMAGES.restaurant3 },
  { country: "Australia", name: "Victoria", tagline: "Melbourne — the home of modern café culture.", image: IMAGES.cafe2 },
  { country: "Japan", name: "Tokyo", tagline: "Precision, reverence and the world's deepest food city.", image: IMAGES.dishClose1 },
  { country: "Singapore", name: "Singapore", tagline: "Hawker soul meets fine-dining ambition.", image: IMAGES.dish4 },
];

export const COVERAGE = [
  { name: "North America", status: "Live", note: "Canada & the USA" },
  { name: "Europe", status: "Live", note: "UK · France · Italy" },
  { name: "Asia–Pacific", status: "Live", note: "Japan · Singapore · Australia" },
  { name: "Worldwide", status: "The vision", note: "Growing, city by city" },
];

export const CATEGORIES = ["All", "Restaurant", "Bar", "Café", "Bakery", "Nightclub", "Hotel", "Outdoor", "Adventure", "Experience", "Winery", "Brewery", "Market", "Dessert", "Shop"];

// ————— HELPERS —————
export function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
export function getPlace(slug) {
  return PLACES.find((p) => p.slug === slug);
}
export function placesFromSlugs(slugs) {
  return slugs.map(getPlace).filter(Boolean);
}
export function regionsInCountry(country) {
  return REGIONS.filter((r) => r.country === country);
}
export function citiesInRegion(country, region) {
  return [...new Set(PLACES.filter((p) => p.country === country && p.region === region).map((p) => p.city))];
}
export function countPlacesInRegion(country, region) {
  return PLACES.filter((p) => p.country === country && p.region === region).length;
}
export function countPlacesInCountry(country) {
  return PLACES.filter((p) => p.country === country).length;
}
export function getRegionMeta(name) {
  return REGIONS.find((r) => slugify(r.name) === slugify(name));
}

// ————— GUIDES —————
export const GUIDES = [
  { id: "g1", slug: "calgary-date-night", title: "The Calgary Date-Night Ten", city: "Calgary", country: "Canada",
    cover: IMAGES.moody1, count: 10,
    dek: "Ten rooms engineered for a good evening — low light, real cooking, and a bill that respects the effort.",
    placeSlugs: ["ox-and-angela", "marquee-cocktail-room", "saffron-and-smoke", "the-hearth-inglewood"],
    author: "The Placeworthy Editors", date: "Jul 2025" },
  { id: "g2", slug: "toronto-wine-nights", title: "Toronto After Dark", city: "Toronto", country: "Canada",
    cover: IMAGES.moody2, count: 8,
    dek: "The wine rooms, bars and late kitchens that make a Toronto night worth staying out for.",
    placeSlugs: ["grange-and-vine", "queen-west-coffee"],
    author: "R. Kowalski", date: "Jul 2025" },
  { id: "g3", slug: "vancouver-by-the-water", title: "Vancouver by the Water", city: "Vancouver", country: "Canada",
    cover: IMAGES.restaurant3, count: 7,
    dek: "Pacific Northwest cooking at its finest, from pristine seafood to a proper Gastown nightcap.",
    placeSlugs: ["harbour-and-cedar", "gastown-pour-house"],
    author: "M. Delacroix", date: "Jun 2025" },
  { id: "g4", slug: "austin-eats", title: "Austin, Start to Finish", city: "Austin", country: "United States",
    cover: IMAGES.chef2, count: 9,
    dek: "Brisket at dawn, cocktails and live music at night — a full day in Texas's most delicious city.",
    placeSlugs: ["smoke-and-barrel", "east-sixth-social"],
    author: "The Placeworthy Editors", date: "Jun 2025" },
  { id: "g5", slug: "new-york-downtown", title: "Downtown New York, Done Right", city: "New York City", country: "United States",
    cover: IMAGES.restaurant2, count: 10,
    dek: "The buzzy rooms and quiet corners worth your limited nights below 14th Street.",
    placeSlugs: ["the-bowery-table", "brooklyn-roasters"],
    author: "R. Kowalski", date: "May 2025" },
  { id: "g6", slug: "coffee-worth-the-detour", title: "Coffee Worth the Detour", city: "Various", country: "Canada",
    cover: IMAGES.cafe3, count: 12,
    dek: "The cafés we cross cities for — meticulous coffee, pastry that matters, rooms you want to stay in.",
    placeSlugs: ["camber-coffee", "queen-west-coffee", "brooklyn-roasters", "fitzroy-roasters", "field-and-flour"],
    author: "S. Nakamura", date: "May 2025" },
  { id: "g7", slug: "tokyo-essentials", title: "Tokyo, for the First Time", city: "Tokyo", country: "Japan",
    cover: IMAGES.dishClose1, count: 11,
    dek: "Where to eat and drink on a first trip to the world's deepest food city — from a Ginza counter to a Golden Gai nightcap.",
    placeSlugs: ["omakase-nishi", "golden-gai-highball"],
    author: "M. Delacroix", date: "Jul 2025" },
  { id: "g8", slug: "london-grown-up", title: "A Grown-Up Night in London", city: "London", country: "United Kingdom",
    cover: IMAGES.moody1, count: 9,
    dek: "The rooms worth dressing up for when you want London at its most quietly excellent.",
    placeSlugs: ["the-marylebone-room"],
    author: "R. Kowalski", date: "Jul 2025" },
];

// ————— AWARDS —————
export const AWARD_YEAR = 2025;
export const AWARDS = [
  { id: "a1", title: "Restaurant of the Year", winnerSlug: "the-hearth-inglewood",
    note: "The most complete dining experience we reviewed all year — a kitchen and a room pulling in perfect unison." },
  { id: "a2", title: "Best New Opening", winnerSlug: "saffron-and-smoke",
    note: "A confident debut that treats its cuisine as living and evolving. The most exciting arrival of the year." },
  { id: "a3", title: "Bar of the Year", winnerSlug: "marquee-cocktail-room",
    note: "Precise drinks, a flawless room, and the most convincing case for one more round." },
  { id: "a4", title: "Best Value", winnerSlug: "smoke-and-barrel",
    note: "Cooking this serious, priced this fairly, is the truest definition of value we know." },
  { id: "a5", title: "Coastal Kitchen of the Year", winnerSlug: "harbour-and-cedar",
    note: "A love letter to the Pacific — pristine seafood in a room that knows how lucky it is." },
  { id: "a6", title: "Hospitality Award", winnerSlug: "maison-lumiere",
    note: "Service that moves like quiet choreography, in one of the finest rooms on the continent." },
];

export const RATING_TIERS = [
  { label: "Placeworthy", range: "9.0 – 10", tone: "top",
    meaning: "Exceptional. We would send someone we love here without hesitation. The highest verdict we give." },
  { label: "Worth It", range: "8.0 – 8.9", tone: "good",
    meaning: "A genuinely rewarding visit. Confident, complete, and well worth your time and money." },
  { label: "Worth a Visit", range: "6.5 – 7.9", tone: "mid",
    meaning: "Good, with clear strengths. Worth going for the right occasion or the right dish." },
  { label: "Situational", range: "5.0 – 6.4", tone: "low",
    meaning: "Uneven. There is something here, but it depends heavily on what you order and when you go." },
  { label: "Skip It", range: "Below 5.0", tone: "skip",
    meaning: "Not yet. We would not send you here — and we will happily revisit if things change." },
];
